import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Col, Input, Row, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import Spinner from "../../common/spinner/spinner";
import { AdminService } from "../../services/AdminService";
import { MeetingData } from "../../models/MeetingData";
import Swal from "sweetalert2";
import moment from "moment";

interface ManuallyAddedSessionsProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const ManuallyAddedSessions: React.FC<ManuallyAddedSessionsProps> = ({ isLoading, setIsLoading }) => {
  const [meetingCount, setMeetingCount] = useState(10);
  const [meetingsAndRecordings, setMeetingsAndRecordings] = useState([] as MeetingData[]);
  const [meetingOffset, setMeetingOffset] = useState(1);
  const [isSearchableText, setSearchableText] = useState({ searchableText: "" } as any);

  // Modal states
  const [reasonModal, setReasonModal] = useState(false);
  const [documentModal, setDocumentModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<MeetingData | null>(null);

  const LIMIT = 10;

  useEffect(() => {
    getAllManuallyAddedSessions();
  }, []);

  const getAllManuallyAddedSessions = () => {
    setIsLoading(true);
    AdminService.getAllManuallyAddedSessions(LIMIT, meetingOffset).then(res => {
      if (res.success) {
        setMeetingsAndRecordings(res.data);
        setMeetingCount(res.data?.length);
        setIsLoading(false);
      } else {
        setMeetingsAndRecordings([] as MeetingData[]);
        setIsLoading(false);
      }
    });
  };

  const seeMoreMeetingsAndRecordings = () => {
    setIsLoading(true);
    const updatedOffset = meetingOffset + 1;
    setMeetingOffset(updatedOffset);
    AdminService.getAllManuallyAddedSessions(LIMIT, updatedOffset).then(res => {
      if (res.success) {
        setMeetingsAndRecordings(previousmeeting => {
          return [...new Set([...previousmeeting, ...res.data])];
        });
        setMeetingCount(res.data?.length);
        setIsLoading(false);
      } else {
        setMeetingsAndRecordings([] as MeetingData[]);
        setIsLoading(false);
      }
    });
  };

  const deleteMeetingsAndRecordings = (id: string) => {
    Swal.fire({
      icon: "warning",
      title: "Are you sure you want to delete?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#FD7F00",
      cancelButtonColor: "#FD7F00",
    }).then(result => {
      if (result.isConfirmed) {
        AdminService.deleteMeetingsAndRecordingById(id).then(res => {
          if (res.success) {
            Swal.fire({
              icon: "success",
              title: "Session deleted!",
              confirmButtonColor: "#FD7F00",
            });
            const refreshedArr = meetingsAndRecordings.filter(item => item._id !== id);
            if (refreshedArr) {
              setMeetingsAndRecordings(refreshedArr);
            }
          } else {
            Swal.fire({
              icon: "error",
              title: "Failed to delete.",
              confirmButtonColor: "#FD7F00",
            });
          }
        });
      }
    });
  };

  const searchMeetingId = () => {
    if (isSearchableText.searchableText) {
      setIsLoading(true);

      AdminService.searchMeetingsAndRecordingsByAdmin(isSearchableText).then(res => {
        if (res.success) {
          setMeetingsAndRecordings(res.data);
          setMeetingCount(res.data?.length);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      });
    } else {
      getAllManuallyAddedSessions();
    }
  };

  const viewReason = (session: MeetingData) => {
    setSelectedSession(session);
    setReasonModal(true);
  };

  const viewDocument = (session: MeetingData) => {
    setSelectedSession(session);
    setDocumentModal(true);
  };

  return (
    <>
      <Row>
        <Col xl="6"></Col>
        <Col xl="6 d-flex">
          <Input
            placeholder="Search from Meeting Id (eg : abcr-cdrr-edfr)"
            className="mb-3 mr-4"
            onChange={(e: any) => setSearchableText({ ...isSearchableText, searchableText: e.target.value })}
          />
          <button className="btn btn-primary mb-3" onClick={searchMeetingId}>
            Search
          </button>
        </Col>
      </Row>
      <Row>
        <Col xl="12">
          {meetingsAndRecordings !== undefined && meetingsAndRecordings.length > 0 ? (
            <>
              <div className="table-responsive" style={{ minHeight: "70vh" }}>
                <table className="table">
                  <thead className="thead-dark">
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Meeting ID</th>
                      <th scope="col">Client Full Name</th>
                      <th scope="col">Therapist Full Name</th>
                      <th scope="col">Date of Session</th>
                      <th scope="col">Session Duration</th>
                      <th scope="col">View Reason</th>
                      <th scope="col">View Documents</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meetingsAndRecordings.length > 0 &&
                      meetingsAndRecordings.map((data: MeetingData, i: number) => {
                        return (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{data.meetingId}</td>
                            <td>
                              <Link to={"/profile/" + data.clientId?._id}>
                                {data?.clientId?.firstname} {data?.clientId?.lastname}
                              </Link>
                            </td>
                            <td>
                              <Link to={"/profile/" + data.therapistId?._id}>
                                {data?.therapistId?.firstname} {data?.therapistId?.lastname}
                              </Link>
                            </td>
                            <td>{data?.regularMeetingDate ? moment(data.regularMeetingDate).format("YYYY-MM-DD") : data.createdAt ? moment(data.createdAt).format("YYYY-MM-DD") : <span>-</span>}</td>
                            <td>{data?.spentDuration != null ? data?.spentDuration + " min" : "--"}</td>
                            <td>
                              <button
                                className="btn btn-info btn-sm"
                                onClick={() => viewReason(data)}
                                disabled={!data.reasonText}
                              >
                                View Reason
                              </button>
                            </td>
                            <td>
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => viewDocument(data)}
                                disabled={!data.downloadUrlProofImagePath}
                              >
                                View Documents
                              </button>
                            </td>
                            <td>
                              <button className="btn btn-warning btn-sm" onClick={() => deleteMeetingsAndRecordings(data._id)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              {meetingCount >= 10 && (
                <>
                  <button className="btn btn-warning text-center mt-4" onClick={seeMoreMeetingsAndRecordings}>
                    {
                      isLoading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        : <>See More</>
                    }
                  </button>
                </>
              )}
              {meetingCount == 0 && <span>No more manually added sessions...</span>}
            </>
          ) : (
            <>{isLoading ? <Spinner /> : <div>No manually added sessions</div>}</>
          )}
        </Col>
      </Row>

      {/* Reason Modal */}
      <Modal isOpen={reasonModal} toggle={() => setReasonModal(false)} centered>
        <ModalHeader toggle={() => setReasonModal(false)}>
          Session Reason
        </ModalHeader>
        <ModalBody>
          <div>
            <h6>Therapist's Reason for Manually Logging Session:</h6>
            <p style={{ marginTop: "10px", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "5px" }}>
              {selectedSession?.reasonText || "No reason provided"}
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setReasonModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Document Modal */}
      <Modal isOpen={documentModal} toggle={() => setDocumentModal(false)} centered size="lg">
        <ModalHeader toggle={() => setDocumentModal(false)}>
          Session Documents
        </ModalHeader>
        <ModalBody>
          <div>
            <h6>Proof of Session Duration:</h6>
            {selectedSession?.downloadUrlProofImagePath ? (
              <div style={{ marginTop: "10px" }}>
                <img
                  src={selectedSession.downloadUrlProofImagePath}
                  alt="Session Proof"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    border: "1px solid #ddd",
                    borderRadius: "5px"
                  }}
                />
                <div style={{ marginTop: "10px" }}>
                  <a
                    href={selectedSession.downloadUrlProofImagePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    Download Document
                  </a>
                </div>
              </div>
            ) : (
              <p style={{ marginTop: "10px", color: "#6c757d" }}>
                No document available
              </p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDocumentModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ManuallyAddedSessions;
